import { Link, useLocation, useNavigate } from "react-router-dom";
import { useState, useEffect } from "react";
import { useAuth } from "@/context/AuthContext";
import { useUnread } from "@/context/UnreadContext";
import { useLang } from "@/context/LanguageContext";
import { SITE_LANGS, Flag } from "@/components/LangFlags";
import AvlianaLogo from "@/components/AvlianaLogo";
import ThemeToggle from "@/components/ThemeToggle";
import { Sheet, SheetContent } from "@/components/ui/sheet";
import { inserierenZiel, roleDestination } from "@/lib/roleRedirect";
import { Menu, X, ChevronDown, ChevronRight, User, KeyRound, Plus } from "lucide-react";
import AppSheet from "@/components/mobile/AppSheet";
import SpracheUndThema from "@/components/mobile/SpracheUndThema";
import useScrollChrome from "@/hooks/useScrollChrome";
import useIsPhone from "@/hooks/useIsPhone";
import { MENUE_EREIGNIS } from "@/lib/menueEreignis";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";

export default function Navbar() {
  const [open, setOpen] = useState(false);
  // Beim Scrollen einen dezenten Schatten setzen (Tiefe/Trennung ggü. dem Inhalt). #12
  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
  const { user, logout } = useAuth();
  const { unread } = useUnread();
  const { lang, switchLanguage, t } = useLang();
  const nav = useNavigate();
  // Kopfzeile weicht beim Runterscrollen (Handoff §7). Werte identisch zu
  // pages/Search.jsx, damit sich beide Koepfe gleich anfuehlen: bis 8 px immer
  // sichtbar, kumuliert > 40 px runter ab y > 80 versteckt, kumuliert > 320 px
  // hoch zeigt wieder. Verfahren (rAF-gedrosselt) in hooks/useScrollChrome.js.
  const versteckt = useScrollChrome({ abY: 80, runter: 40, hoch: 320 });
  // Handy (< 768 px) = App-Kopf: Menü als Bottom-Sheet im App-Look statt des
  // rechten Seitenpanels. Tablet behält das Panel (Entscheidung 01.10.2026).
  const phone = useIsPhone();
  const loc = useLocation();
  // Seiten mit EIGENEM App-Kopf am Handy — dort weicht dieser Kopf unter
  // 768 px, wie in der App:
  //  · Suche: Marke + Karten-/Sortier-/Menüknopf im Suchkopf; das Menü
  //    öffnet sie über MENUE_EREIGNIS.
  //  · Inserat und Projekt: das Foto ist der Kopf, darüber runde Knöpfe
  //    (Zurück · Merken · Teilen · ⋯).
  //  · Geöffnete Unterhaltung (/nachrichten/<id>): Vollbild mit eigenem Kopf.
  const eigenerHandyKopf = ["/suche", "/immobilie/", "/projekt/", "/nachrichten/"].some((p) => loc.pathname.startsWith(p));
  useEffect(() => {
    const oeffnen = () => setOpen(true);
    window.addEventListener(MENUE_EREIGNIS, oeffnen);
    return () => window.removeEventListener(MENUE_EREIGNIS, oeffnen);
  }, []);

  const links = [
    { to: "/kaufen", label: t("nav.buy") },
    { to: "/mieten", label: t("nav.rent") },
    { to: "/neubau", label: t("nav.new_builds") },
    { to: "/wertermittlung", label: t("nav.valuation") },
    { to: "/finanzierung", label: t("nav.financing") },
  ];

  const currentLang = SITE_LANGS.find((l) => l.code === lang) || SITE_LANGS.find((l) => l.code === "en");

  const authed = user && typeof user === "object";
  // Makler und Bauträger inserieren nie kostenlos — ihnen „Kostenlos
  // inserieren" anzuzeigen wäre ein falsches Versprechen (Nic, 10.08.). Sie
  // springen direkt in den Assistenten. Zentrale Weiche statt Inline-Regel:
  // das Kontomenü unten schickte Gewerbliche sonst hart auf /pakete.
  const gewerblich = authed && (user.role === "agent" || user.role === "developer");
  const ctaTo = inserierenZiel(authed ? user : null);
  // Zentrale Rollenweiche statt eigener Liste: Die hier fehlende
  // developer-Verzweigung schickte Bauträger ins Privat-Dashboard.
  const dashboardTo = roleDestination(authed ? user : null);

  // Kopfleiste als FLÄCHE, nicht als Glas. Sättigungsanhebung und die zwei
  // Inset-Lichtkanten simulierten eine Glasscheibe über dem Inhalt — zusammen mit
  // den 68 % Deckkraft war das der zweite Ort (neben der unteren Leiste), an dem
  // die Seite nach Baukasten aussah. 92 % Deckkraft und ein leichter Blur genügen,
  // damit sie sich vom Inhalt absetzt; beim Scrollen kommt eine Trennlinie dazu
  // statt eines Leuchtens.
  return (
    // Handy: App-Kopf (mobile/components/BrandHeader.tsx) — weiße Fläche wie
    // der App-Seitengrund, ohne Trennlinie, 56 px hoch, 20 px Rand. Ab md
    // alles wie bisher (jede Kopf-Optik dort mit md: gesetzt).
    <header className={`${eigenerHandyKopf ? "max-md:hidden " : ""}sticky top-0 z-50 bg-appbg md:backdrop-blur-md md:bg-surface/[0.92] md:border-b md:border-anthracite/[0.06] transition-[transform,box-shadow] duration-300 ${versteckt ? "-translate-y-full" : "translate-y-0"} ${scrolled ? "shadow-[0_1px_0_rgba(45,45,45,0.06)] md:shadow-[0_1px_10px_rgba(45,45,45,0.06)] dark:shadow-[0_1px_0_rgba(255,255,255,0.06)] md:dark:shadow-[0_1px_10px_rgba(0,0,0,0.4)]" : ""}`}>
      <div className="max-w-7xl mx-auto px-5 md:px-6 lg:px-8 flex items-center justify-between h-14 md:h-16 lg:h-20">
        {/* Logo */}
        {/* flex-shrink-0 + me-6: Ohne beides schob sich die Navigation bei knapper
            Breite bis auf 0 px an die Marke heran — gemessen ab 1152 px klebte
            „Kaufen" am Logo. Ein Logo, das sich stauchen laesst, ist kein Logo. */}
        <Link to="/" data-testid="nav-logo" className="flex flex-shrink-0 items-center gap-2.5 me-6 min-h-11 md:min-h-0">
          {/* Bogen etwas hoeher als die Versalhoehe, damit Zeichen und Wortmarke
              optisch auf einer Linie sitzen. Regelwerk: memory/BRAND_AVLIANA.md */}
          <AvlianaLogo className="h-[22px] md:h-[27px] w-auto" />
          {/* Groesse laut Handoff: 25px Web / 20px Handy. Gewicht bewusst 400
              (Standard der Serif) — das Logo soll nicht fett stehen. */}
          <span className="font-heading text-[20px] md:text-[25px] text-anthracite">
            Avliana
          </span>
        </Link>

        {/* Links (desktop) */}
        {/* xl statt lg: Fuenf Navigationspunkte plus Sprachpille, Erscheinungs-
            schalter, Konto und CTA brauchen mehr als 1024 px. Gemessen bleiben bei
            1280 px noch 12 px kleinste Luecke, bei 1152 px sind es 0 — darunter
            uebernimmt das Burger-Menue, das es ohnehin gibt. */}
        <nav className="hidden xl:flex items-center gap-8">
          {/* Aktiv = feiner Unterstrich statt nur Farbe; Hover blendet ihn sanft
              ein (200 ms). Kein neues Element, nur ::after. */}
          {links.map((l) => {
            const active = loc.pathname.startsWith(l.to);
            return (
              <Link
                key={l.to}
                to={l.to}
                data-testid={`nav-link-${l.to.replace("/", "")}`}
                className={`relative whitespace-nowrap py-1 text-sm font-body transition-colors duration-200 hover:text-mediterranean after:absolute after:inset-x-0 after:-bottom-0.5 after:h-px after:bg-mediterranean after:origin-center after:transition-transform after:duration-200 ${active ? "text-mediterranean after:scale-x-100" : "text-anthracite/75 after:scale-x-0 hover:after:scale-x-100"}`}
              >
                {l.label}
              </Link>
            );
          })}
        </nav>

        {/* Right */}
        <div className="hidden xl:flex items-center gap-3">
          {/* Lang switch — Flaggen, Reihenfolge EN · EL · DE · RU · AR · HE, volle Namen + Kürzel */}
          <DropdownMenu>
            <DropdownMenuTrigger data-testid="lang-switcher" className="inline-flex items-center gap-2 h-9 px-3 rounded-control border border-anthracite/10 bg-surface/40 hover:bg-surface/80 hover:border-anthracite/25 transition-colors duration-200 text-xs font-semibold uppercase tracking-[0.12em] text-anthracite">
              {/* Kuerzel, nicht der volle Name. Der Handoff wollte hier
                  „Deutsch" — gebaut und am 30.07. wieder zurueckgenommen: Die
                  Pille ist auf zwei Zeichen ausgelegt (h-9, text-xs, uppercase,
                  tracking .12em). Ein langes Wort mit eigener Schreibweise und
                  eigenem Zeichenabstand sprengt sie, und weil die Pille in JEDER
                  Kopfzeile steht, faellt das auf der ganzen Seite auf. Die
                  vollen Namen stehen im Aufklapper, wo Platz dafuer ist. */}
              <Flag code={currentLang.code} /> {currentLang.code.toUpperCase()} <ChevronDown className="w-3 h-3 text-anthracite/40" />
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              {SITE_LANGS.map((l) => (
                <DropdownMenuItem key={l.code} data-testid={`lang-${l.code}`} onClick={() => switchLanguage(l.code)} className="gap-2.5">
                  <Flag code={l.code} /> <span className="flex-1">{l.label}</span>
                  <span className="font-mono text-[9px] tracking-widest text-olive">{l.code.toUpperCase()}</span>
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>

          {/* Hier stand bis zum 04.08.2026 der Hell/Dunkel-Schalter. Die Pille
              ist 96 px breit; zusammen mit Sprachwähler, Nutzernamen und dem
              Primärknopf wurde die Zeile so eng, dass „Kostenlos inserieren"
              auf zwei Zeilen umbrach und die Abstände kippten. Ein Schalter,
              den man einmal einstellt, gehört nicht auf den teuersten Platz
              der Seite — er steht jetzt im Fuß neben der Sprachwahl
              (Footer.jsx) und weiterhin im Mobilmenü weiter unten. */}

          {!authed ? (
            <>
              <Link to="/login" data-testid="nav-login" className="text-xs text-anthracite/70 hover:text-mediterranean font-body tracking-wide">{t("nav.login")}</Link>
              <span className="text-anthracite/20 text-xs">·</span>
              <Link to="/register" data-testid="nav-register" className="text-xs text-anthracite/70 hover:text-mediterranean font-body tracking-wide">{t("nav.register")}</Link>
            </>
          ) : (
            <DropdownMenu>
              <DropdownMenuTrigger data-testid="user-menu" className="flex items-center gap-2 text-sm text-anthracite">
                <span className="relative w-8 h-8 rounded-full bg-olive/20 grid place-items-center">
                  <User className="w-4 h-4 text-olive" />
                  {/* Ungelesen-Badge: sichtbar ohne das Menü zu öffnen — vorher
                      erfuhr man außerhalb des Dashboards nie von neuen Antworten. */}
                  {unread > 0 && (
                    <span
                      data-testid="nav-unread-badge"
                      className="absolute -top-1 -end-1 min-w-[18px] h-[18px] px-1 rounded-full bg-terracotta text-white text-[10px] font-semibold leading-none grid place-items-center"
                    >
                      {unread > 99 ? "99+" : unread}
                    </span>
                  )}
                </span>
                <span className="max-w-[120px] truncate">{user.name}</span>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={() => nav(dashboardTo)} data-testid="menu-dashboard">{t("nav.dashboard")}</DropdownMenuItem>
                <DropdownMenuItem onClick={() => nav("/nachrichten")} data-testid="menu-messages" className="justify-between gap-3">
                  {t("nav.messages")}
                  {unread > 0 && <span className="min-w-[18px] h-[18px] px-1 rounded-full bg-terracotta text-white text-[10px] font-semibold leading-none grid place-items-center">{unread > 99 ? "99+" : unread}</span>}
                </DropdownMenuItem>
                {/* Gleiche Rollenweiche wie der Haupt-CTA — vorher hart /pakete,
                    was eingeloggte Gewerbliche auf die private Slot-Seite schickte. */}
                <DropdownMenuItem onClick={() => nav(inserierenZiel(user))} data-testid="menu-create-listing">{t("nav.list_property")}</DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={logout} data-testid="menu-logout">{t("nav.logout")}</DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          )}

          {/* Hochwertiger Primär-Button: Lichtkante oben, weicher Terrakotta-
              Schatten, ruhige Anhebung (lift, 200 ms).
              Text und Schlüsselsymbol sind bewusst DIESELBEN wie im Knopf der
              Suchbox (HeroSearch.jsx) — beide führen auf /pakete. Vorher stand
              hier „Immobilie inserieren" ohne Symbol, darunter „Kostenlos
              inserieren" mit Schlüssel: zwei Beschriftungen für denselben Weg,
              und die kostenlose Variante war ausgerechnet oben nicht sichtbar.
              Ausnahme: eingeloggte Gewerbliche (siehe `gewerblich` oben). */}
          <Link
            to={ctaTo}
            data-testid="nav-cta-list"
            className="lift inline-flex items-center gap-2 whitespace-nowrap shrink-0 rounded-control px-5 py-2.5 bg-terracotta text-white text-sm font-medium hover:bg-terracotta-hover transition-colors duration-200 shadow-[inset_0_1px_0_rgba(255,255,255,0.25),0_6px_16px_-6px_rgba(188,74,43,0.45)]"
          >
            {gewerblich
              ? <><Plus className="w-4 h-4" /> {t("bnav.list")}</>
              : <><KeyRound className="w-4 h-4" /> {t("home.cta_free_listing")}</>}
          </Link>
        </div>

        {/* Handy: runder 40er-Knopf in surface2 wie der Menüknopf im
            App-Profil. Tablet (md–xl): der bisherige Burger, unverändert. */}
        <button
          data-testid="phone-menu-toggle"
          aria-label={t("nav.menu")}
          aria-expanded={open}
          className="md:hidden grid place-items-center w-10 h-10 rounded-full bg-surface2 text-anthracite active:bg-surface3 transition-colors"
          onClick={() => setOpen(!open)}
        >
          {open ? <X className="w-5 h-5" strokeWidth={2.2} /> : <Menu className="w-5 h-5" strokeWidth={2.2} />}
        </button>
        <button data-testid="mobile-menu-toggle" aria-label={t("nav.menu")} aria-expanded={open} className="hidden md:block xl:hidden -me-2 p-2.5 text-anthracite" onClick={() => setOpen(!open)}>
          {open ? <X /> : <Menu />}
        </button>
      </div>

      {/* Mobile-Menü als Sheet (Radix-Dialog: Fokus-Trap, ESC, Tap-außen, Scroll-Lock,
          Overlay) statt Inline-Dropdown ohne Scroll-Lock. #11 */}
      {phone && (
        <AppSheet open={open} onOpenChange={setOpen} title={t("nav.menu")} titleSichtbar={false} testId="mobile-menu-sheet">
          <HandyMenue
            links={links}
            authed={authed}
            dashboardTo={dashboardTo}
            ctaTo={ctaTo}
            gewerblich={gewerblich}
            logout={logout}
            schliessen={() => setOpen(false)}
          />
        </AppSheet>
      )}
      <Sheet open={open && !phone} onOpenChange={setOpen}>
        <SheetContent side="right" aria-label={t("nav.menu")} className="xl:hidden w-[86vw] max-w-sm overflow-y-auto">
          <div className="flex flex-col gap-4 mt-6">
            {links.map((l) => (
              <Link key={l.to} to={l.to} onClick={() => setOpen(false)} className="text-anthracite min-h-[44px] flex items-center">{l.label}</Link>
            ))}
            <div className="border-t border-anthracite/10 pt-4 flex flex-col gap-3">
              {!authed ? (
                <>
                  <Link to="/login" onClick={() => setOpen(false)} className="text-anthracite min-h-[44px] flex items-center">{t("nav.login")}</Link>
                  <Link to="/register" onClick={() => setOpen(false)} className="text-anthracite min-h-[44px] flex items-center">{t("nav.register")}</Link>
                </>
              ) : (
                <>
                  <Link to={dashboardTo} onClick={() => setOpen(false)} className="text-anthracite min-h-[44px] flex items-center">{t("nav.dashboard")}</Link>
                  <button onClick={() => { logout(); setOpen(false); }} className="text-start text-anthracite min-h-[44px] flex items-center">{t("nav.logout")}</button>
                </>
              )}
              {/* Gleiche Weiche wie der Desktop-CTA: Gewerbliche direkt in den
                  Assistenten, alle anderen auf die Paketseite. */}
              <Link to={ctaTo} onClick={() => setOpen(false)} className="rounded-control px-5 py-3 bg-terracotta text-white text-center">
                {gewerblich ? t("bnav.list") : t("nav.list_property")}
              </Link>
            </div>
            {/* Sprache — mobil gab es bisher keinen Umschalter (der Desktop-
                Dropdown ist lg-only, der Footer weit weg). Menü bleibt nach dem
                Wechsel offen: Man sieht die Umstellung sofort an den Einträgen. */}
            <div className="border-t border-anthracite/10 pt-4">
              <div className="kicker text-anthracite/70 mb-3">{t("footer.lang_label")}</div>
              <div className="flex flex-wrap gap-2" data-testid="mobile-lang-switcher">
                {SITE_LANGS.map((l) => (
                  <button
                    key={l.code}
                    type="button"
                    data-testid={`mobile-lang-${l.code}`}
                    onClick={() => switchLanguage(l.code)}
                    className={`inline-flex items-center gap-2 px-3.5 py-2 rounded-control border text-xs font-semibold uppercase tracking-[0.1em] transition-colors ${lang === l.code ? "border-mediterranean bg-mediterranean/5 text-mediterranean" : "border-anthracite/15 text-anthracite/70"}`}
                  >
                    <Flag code={l.code} /> {l.code}
                  </button>
                ))}
              </div>
            </div>
            {/* Light/Dark — derselbe Sonne/Mond-Schalter wie im Desktop, als
                eigene Zeile unter der Sprache (volle Breite). Menü bleibt
                offen: Der Wechsel ist sofort an der Oberfläche sichtbar. */}
            <div className="border-t border-anthracite/10 pt-4">
              <div className="kicker text-anthracite/70 mb-3">{t("theme.label")}</div>
              <ThemeToggle testId="mobile-theme-toggle" wide />
            </div>
          </div>
        </SheetContent>
      </Sheet>
    </header>
  );
}

// ---------------------------------------------------------------------------
// Menü am Handy als App-Sheet: gruppierte Zeilen wie die Menügruppen im
// App-Profil (Karte, 52er-Zeilen, Chevron), darunter Inserieren als Pille,
// Sprache als Pillen (mobile/components/LanguageSwitcher.tsx) und Hell/Dunkel.
// Inhalt identisch zum Tablet-Panel — nur die Form folgt der App.
// ---------------------------------------------------------------------------
function MenueGruppe({ children }) {
  return (
    <div className="mx-5 rounded-[16px] border border-anthracite/10 bg-surface px-4 divide-y divide-anthracite/10">
      {children}
    </div>
  );
}

function MenueZeile({ to, onClick, children, testId }) {
  const inhalt = (
    <>
      <span className="flex-1 text-[16px] font-medium">{children}</span>
      <ChevronRight className="w-4 h-4 text-appmuted rtl:rotate-180" strokeWidth={2.2} />
    </>
  );
  const cls = "flex min-h-[52px] w-full items-center gap-3 text-start text-anthracite active:opacity-70";
  return to
    ? <Link to={to} onClick={onClick} data-testid={testId} className={cls}>{inhalt}</Link>
    : <button type="button" onClick={onClick} data-testid={testId} className={cls}>{inhalt}</button>;
}

function HandyMenue({ links, authed, dashboardTo, ctaTo, gewerblich, logout, schliessen }) {
  const { t } = useLang();
  return (
    <div className="flex flex-col gap-5 pb-2">
      <MenueGruppe>
        {links.map((l) => (
          <MenueZeile key={l.to} to={l.to} onClick={schliessen}>{l.label}</MenueZeile>
        ))}
      </MenueGruppe>

      <MenueGruppe>
        {!authed ? (
          <>
            <MenueZeile to="/login" onClick={schliessen} testId="phone-menu-login">{t("nav.login")}</MenueZeile>
            <MenueZeile to="/register" onClick={schliessen}>{t("nav.register")}</MenueZeile>
          </>
        ) : (
          <>
            <MenueZeile to={dashboardTo} onClick={schliessen}>{t("nav.dashboard")}</MenueZeile>
            <MenueZeile onClick={() => { logout(); schliessen(); }}>{t("nav.logout")}</MenueZeile>
          </>
        )}
      </MenueGruppe>

      {/* Gleiche Weiche wie der Desktop-CTA: Gewerbliche direkt in den
          Assistenten, alle anderen auf die Paketseite. Pille = App-Button. */}
      <div className="px-5">
        <Link
          to={ctaTo}
          onClick={schliessen}
          className="flex h-12 w-full items-center justify-center rounded-full bg-terracotta px-7 text-[16px] font-semibold text-white active:bg-terracotta-hover"
        >
          {gewerblich ? t("bnav.list") : t("nav.list_property")}
        </Link>
      </div>

      {/* Sprache und Hell/Dunkel — dieselbe Komponente wie unten im Profil.
          Das Sheet bleibt nach dem Wechsel offen: Man sieht die Umstellung
          sofort an den Einträgen. */}
      <div className="flex flex-col gap-5 px-5">
        <SpracheUndThema />
      </div>

      {/* Abbrechen-Pille wie unter jedem App-ActionSheet. */}
      <div className="px-5">
        <button type="button" onClick={schliessen} className="w-full rounded-full bg-surface2 py-3.5 text-[16px] font-semibold text-anthracite active:bg-surface3">
          {t("common.close")}
        </button>
      </div>
    </div>
  );
}
